(function () {
  const SECTION_CLASS = "category-showcase-section";
  const DESKTOP_FALLBACK = "990px";

  class CategoryShowcase {
    constructor(el) {
      this.el = el;
      this.tabs = el.querySelector("[data-category-tabs]");
      this.items = Array.from(el.querySelectorAll("[data-category-item]"));
      this.backgrounds = Array.from(
        el.querySelectorAll("[data-category-background]")
      );
      this.controller = new AbortController();
      this.measureFrame = null;

      const breakpoint =
        window
          .getComputedStyle(el)
          .getPropertyValue("--category-showcase-breakpoint")
          .trim() || DESKTOP_FALLBACK;
      this.desktopQuery = window.matchMedia(`(min-width: ${breakpoint})`);

      this.items.forEach((item) => this._bindItem(item));

      // The panel opens against a measured height, and the frame reserves room for the
      // tallest of them, so both come out of one pass — and a fresh one per width.
      this._measure();

      window.addEventListener("resize", () => this._remeasure(), {
        signal: this.controller.signal,
      });
    }

    activate(index) {
      const item = this.items.find(
        (el) => Number(el.dataset.categoryIndex) === index
      );
      if (!item || item.classList.contains("active")) return false;

      // One synchronous block from here down: the panel that opens, the panel that
      // closes and the background swap all start in the same frame.
      this.items.forEach((el) => {
        const isActive = el === item;
        el.classList.toggle("active", isActive);

        const trigger = el.querySelector("[data-category-trigger]");
        if (!trigger) return;

        trigger.setAttribute("aria-expanded", String(isActive));
        if (isActive) {
          trigger.setAttribute("aria-current", "true");
        } else {
          trigger.removeAttribute("aria-current");
        }
      });

      const background = this.backgrounds.find(
        (el) => Number(el.dataset.categoryIndex) === index
      );
      this.backgrounds.forEach((el) =>
        el.classList.toggle("active", el === background)
      );

      if (background) {
        // Remove + reflow + add — a same-frame re-add never restarts a keyframe. Only
        // the incoming layer is reset: a reveal cut short by the next category keeps
        // opening beneath the new one, where clearing the class would show its whole
        // frame in one frame as a pointer sweeps the list.
        background.classList.remove("is-revealing");
        void background.offsetWidth;
        background.classList.add("is-revealing");
      }

      return true;
    }

    destroy() {
      window.cancelAnimationFrame(this.measureFrame);
      this.controller.abort();
    }

    // Reads every tab in one pass, then writes: each panel gets the height it opens to,
    // and the frame gets the room the tallest one needs. Reading the clip rather than
    // the panel is what makes this safe to run mid-animation — the clip keeps its full
    // layout height behind the clamp, so it reports the same value at any point of a
    // switch, while the panel itself reports only the part currently on screen.
    _measure() {
      if (!this.tabs) return;

      this.tabs.style.removeProperty("--category-showcase-tabs-min-height");
      // Nothing opens below the breakpoint, so the measured state comes off the
      // elements rather than staying on them for a width that never animates.
      if (!this.desktopQuery.matches) {
        this.items.forEach((item) => {
          const media = item.querySelector("[data-category-media]");
          if (!media) return;
          media.style.removeProperty("--category-showcase-media-max-height");
        });
        return;
      }

      const tabs = this.items.map((item) => {
        const media = item.querySelector("[data-category-media]");
        const clip = item.querySelector("[data-category-media-clip]");

        return {
          media,
          open: clip ? Math.ceil(clip.getBoundingClientRect().height) : 0,
          shown: media ? media.getBoundingClientRect().height : 0,
          height: item.getBoundingClientRect().height,
        };
      });
      const framed = this.tabs.getBoundingClientRect().height;
      const isRow = window.getComputedStyle(this.tabs).flexDirection === "row";

      // Pills stand side by side on one baseline, so the row only has to hold the
      // tallest single open tab; stacked, the frame holds the closed list plus one.
      let reserved;
      if (isRow) {
        reserved = tabs.reduce(
          (max, tab) => Math.max(max, tab.height - tab.shown + tab.open),
          0
        );
      } else {
        const shown = tabs.reduce((total, tab) => total + tab.shown, 0);
        const tallest = tabs.reduce((max, tab) => Math.max(max, tab.open), 0);
        reserved = framed - shown + tallest;
      }

      tabs.forEach((tab) => {
        if (!tab.media) return;
        tab.media.style.setProperty(
          "--category-showcase-media-max-height",
          `${tab.open}px`
        );
      });

      if (reserved > 0) {
        this.tabs.style.setProperty(
          "--category-showcase-tabs-min-height",
          `${Math.ceil(reserved)}px`
        );
      }
    }

    _remeasure() {
      window.cancelAnimationFrame(this.measureFrame);
      this.measureFrame = window.requestAnimationFrame(() => this._measure());
    }

    _bindItem(item) {
      const index = Number(item.dataset.categoryIndex || 0);
      const trigger = item.querySelector("[data-category-trigger]");
      if (!trigger) return;

      const onHover = () => {
        if (!this.desktopQuery.matches) return;
        this.activate(index);
      };

      item.addEventListener("mouseenter", onHover, {
        signal: this.controller.signal,
      });
      trigger.addEventListener("focusin", onHover, {
        signal: this.controller.signal,
      });
    }
  }

  function destroySection(section) {
    if (!section || typeof section.querySelectorAll !== "function") return;

    section.querySelectorAll(".category-showcase").forEach((el) => {
      if (!el._categoryShowcaseInstance) return;
      el._categoryShowcaseInstance.destroy();
      el._categoryShowcaseInstance = null;
    });
  }

  function initSection(section) {
    if (!section || !section.classList.contains(SECTION_CLASS)) return;

    const root = section.querySelector(".category-showcase");
    if (!root) return;

    if (root._categoryShowcaseInstance) {
      root._categoryShowcaseInstance.destroy();
      root._categoryShowcaseInstance = null;
    }
    root._categoryShowcaseInstance = new CategoryShowcase(root);
  }

  initSection(document.currentScript.parentElement);

  if (!window.__themeCategoryShowcaseEvents) {
    window.__themeCategoryShowcaseEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      destroySection(event.target);
    });
  }
})();
